import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Typography,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material';
import { 
  Add, 
  Edit, 
  Delete, 
  GetApp, 
  Print, 
  Close,
  PostAdd,
  Undo,
  Refresh
} from '@mui/icons-material';
import ServiceFactory from '../services/ApiService';
import { Modules, fmtMoney } from '../data/dummyData';

// Utility function to compute totals from lines
const computeTotals = (doc) => {
  const lines = doc.lines || [];
  const sub = lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unitPrice || 0), 0);
  const tax = lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unitPrice || 0) * Number(l.taxRate || 0) / 100, 0);
  const total = sub + tax;
  return { sub, tax, total };
};

// Loading Component
const LoadingSpinner = ({ message = "Loading..." }) => (
  <Box display="flex" justifyContent="center" alignItems="center" p={4}>
    <CircularProgress size={24} sx={{ mr: 2 }} />
    <Typography variant="body2">{message}</Typography>
  </Box>
);

// Error Component
const ErrorAlert = ({ error, onRetry }) => (
  <Alert 
    severity="error" 
    sx={{ mt: 2 }}
    action={
      onRetry && (
        <Button color="inherit" size="small" onClick={onRetry}>
          Retry
        </Button>
      )
    }
  >
    {error}
  </Alert>
);

// Document Form Component with API Integration
const DocumentForm = ({ moduleKey, initialData, open, onClose, onSave, loading = false }) => {
  const moduleConfig = Modules[moduleKey];
  const service = ServiceFactory.getService(moduleKey);
  
  const [formData, setFormData] = useState({
    billNumber: '', 
    invoiceNumber: '',
    paymentNumber: '',
    receiptNumber: '',
    creditNoteNumber: '',
    debitNoteNumber: '',
    vendorId: null,
    customerId: null,
    billDate: '', 
    invoiceDate: '',
    paymentDate: '',
    receiptDate: '',
    creditNoteDate: '',
    debitNoteDate: '',
    dueDate: '', 
    lines: [{ description: '', quantity: 1, unitPrice: 0, taxRate: 0 }], 
    status: 'Draft',
    reference: '',
    referenceDocument: '',
    description: '',
    paymentMethod: '',
    amount: 0
  });
  
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);

  // Load partner data (vendors/customers) when form opens
  useEffect(() => {
    const loadPartnerData = async () => {
      if (!open) return;
      
      try {
        // Load vendors if needed
        if (moduleKey === 'vendor_bills' || moduleKey === 'vendor_payments' || moduleKey === 'debit_notes') {
          const vendorService = ServiceFactory.getService('vendors');
          const vendorResponse = await vendorService.getAll({ pageSize: 1000 });
          if (vendorResponse.success) {
            setVendors(vendorResponse.data || []);
          }
        }
        
        // Load customers if needed
        if (moduleKey === 'customer_invoices' || moduleKey === 'customer_receipts' || moduleKey === 'credit_notes') {
          const customerService = ServiceFactory.getService('customers');
          const customerResponse = await customerService.getAll({ pageSize: 1000 });
          if (customerResponse.success) {
            setCustomers(customerResponse.data || []);
          }
        }
      } catch (error) {
        console.error('Failed to load partner data:', error);
      }
    };

    loadPartnerData();
  }, [open, moduleKey]);

  useEffect(() => {
    if (open) {
      if (initialData) {
        console.log('Initial data:', initialData);
        
        // Map API data to form structure
        const mappedData = {
          ...initialData,
          dueDate: initialData?.dueDate?.split('T')[0] || '',
          invoiceDate: initialData?.invoiceDate?.split('T')[0] || '',
          billDate: initialData?.billDate?.split('T')[0] || '',
          paymentDate: initialData?.paymentDate?.split('T')[0] || '',
          receiptDate: initialData?.receiptDate?.split('T')[0] || '',
          creditNoteDate: initialData?.creditNoteDate?.split('T')[0] || '',
          debitNoteDate: initialData?.debitNoteDate?.split('T')[0] || '',
          lines: initialData.lines || [{ description: '', quantity: 1, unitPrice: 0, taxRate: 0 }]
        };
        setFormData(mappedData);
      } else {
        // Generate new document with default values
        const today = new Date().toISOString().split('T')[0];
        const newDoc = {
          billNumber: moduleKey === 'vendor_bills' ? '' : undefined,
          invoiceNumber: moduleKey === 'customer_invoices' ? '' : undefined,
          paymentNumber: moduleKey === 'vendor_payments' ? '' : undefined,
          receiptNumber: moduleKey === 'customer_receipts' ? '' : undefined,
          creditNoteNumber: moduleKey === 'credit_notes' ? '' : undefined,
          debitNoteNumber: moduleKey === 'debit_notes' ? '' : undefined,
          vendorId: null,
          customerId: null,
          creditNoteDate: moduleKey === 'credit_notes' ? today : undefined,
          debitNoteDate: moduleKey === 'debit_notes' ? today : undefined,
          billDate: moduleKey === 'vendor_bills' ? today : undefined,
          invoiceDate: moduleKey === 'customer_invoices' ? today : undefined,
          paymentDate: moduleKey === 'vendor_payments' ? today : undefined,
          receiptDate: moduleKey === 'customer_receipts' ? today : undefined,
          dueDate: '', 
          lines: moduleKey.includes('payment') || moduleKey.includes('receipt') ? 
                 [] : [{ description: '', quantity: 1, unitPrice: 0, taxRate: 0 }], 
          status: 'Draft',
          reference: '',
          referenceDocument: '',
          description: '',
          paymentMethod: '',
          amount: 0,
          companyId: 1 // Default company ID
        };
        setFormData(newDoc);
      }
      setFormError('');
    }
  }, [open, initialData, moduleKey]);

  const handleLineChange = (index, field, value) => {
    const newLines = [...formData.lines];
    const numericFields = ['quantity', 'unitPrice', 'taxRate'];
    newLines[index][field] = numericFields.includes(field) ? Number(value) || 0 : value;
    setFormData(prev => ({ ...prev, lines: newLines }));
  };

  const addLine = () => {
    setFormData(prev => ({
      ...prev,
      lines: [...prev.lines, { description: '', quantity: 1, unitPrice: 0, taxRate: 0 }]
    }));
  };

  const removeLine = (index) => {
    if (formData.lines.length > 1) {
      setFormData(prev => ({
        ...prev,
        lines: prev.lines.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async () => {
    if (formLoading) return;
    
    try {
      setFormLoading(true);
      setFormError('');
      
      // Process data for API
      const processedData = {
        ...formData,
        lines: formData.lines.filter(line => line.description?.trim() || line.quantity || line.unitPrice)
      };

      // Calculate totals
      const totals = computeTotals(processedData);
      processedData.subtotalAmount = totals.sub;
      processedData.taxAmount = totals.tax;
      processedData.totalAmount = totals.total;
      processedData.amount = totals.total;

      await onSave(processedData);
    } catch (error) {
      setFormError(error.message || 'Failed to save document');
    } finally {
      setFormLoading(false);
    }
  };

  const totals = computeTotals(formData);
  const showLines = !moduleKey.includes('payment') && !moduleKey.includes('receipt');

  // Get document number field name based on module
  const getNumberField = () => {
    switch (moduleKey) {
      case 'vendor_bills': return 'billNumber';
      case 'customer_invoices': return 'invoiceNumber';
      case 'vendor_payments': return 'paymentNumber';
      case 'customer_receipts': return 'receiptNumber';
      case 'credit_notes': return 'creditNoteNumber';
      case 'debit_notes': return 'debitNoteNumber';
      default: return 'number';
    }
  };

  // Get date field name based on module
  const getDateField = () => {
    switch (moduleKey) {
      case 'vendor_bills': return 'billDate';
      case 'customer_invoices': return 'invoiceDate';
      case 'vendor_payments': return 'paymentDate';
      case 'customer_receipts': return 'receiptDate';
      case 'credit_notes': return 'creditNoteDate';
      case 'debit_notes': return 'debitNoteDate';
      default: return 'date';
    }
  };

  const getReferenceField = () => {
    // For most documents, the API expects 'referenceDocument'
    if (moduleKey === 'credit_notes' || moduleKey === 'debit_notes') {
      return 'referenceDocument';
    }
    // For others, it's just 'reference'
    return 'reference';
  };

  const numberField = getNumberField();
  const dateField = getDateField();
  const referenceField = getReferenceField(); 
  const partnerField = moduleKey.includes('debit') || moduleKey.includes('vendor') ? 'vendorId' : 'customerId';
  const partnerLabel = moduleKey.includes('debit') || moduleKey.includes('vendor') ? 'Vendor' : 'Customer';
  const partnerOptions = moduleKey.includes('debit') || moduleKey.includes('vendor') ? vendors : customers;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {initialData ? 'Edit' : 'New'} {moduleConfig?.title?.slice(0, -1) || 'Document'}
          <IconButton onClick={onClose} size="small" disabled={formLoading}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}

        {/* Header Information */}
        <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={2} mb={3}>
          <Box gridColumn="span 4">
            <TextField
              fullWidth
              size="small"
              label="Document Number"
              value={formData[numberField] || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, [numberField]: e.target.value }))}
            />
          </Box>
          <Box gridColumn="span 4">
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Date"
              value={formData[dateField] || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, [dateField]: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box gridColumn="span 4">
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Due Date"
              value={formData.dueDate || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box gridColumn="span 6">
            <FormControl fullWidth size="small">
              <InputLabel>{partnerLabel}</InputLabel>
              <Select
                value={formData[partnerField] || ''} 
                label={partnerLabel}
                onChange={(e) => setFormData(prev => ({ ...prev, [partnerField]: e.target.value }))}
              >
                <MenuItem value="">Select...</MenuItem>
                {partnerOptions.map(partner => (
                  <MenuItem key={partner.id} value={partner.id}>
                    {partner.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box gridColumn="span 6">
            <TextField
              fullWidth
              size="small"
              label="Reference"
              value={formData[referenceField] || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, [referenceField]: e.target.value }))}
              placeholder="Reference number or notes"
            />
          </Box>
          
          {/* Payment/Receipt specific fields */}
          {(moduleKey.includes('payment') || moduleKey.includes('receipt')) && (
            <>
              <Box gridColumn="span 4">
                <FormControl fullWidth size="small">
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    value={formData.paymentMethod || ''}
                    label="Payment Method"
                    onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value }))}
                  >
                    <MenuItem value="Cash">Cash</MenuItem>
                    <MenuItem value="Bank">Bank Transfer</MenuItem>
                    <MenuItem value="Cheque">Cheque</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box gridColumn="span 4">
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Amount"
                  value={formData.amount || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: Number(e.target.value) }))}
                  inputProps={{ min: 0, step: 'any' }}
                />
              </Box>
            </>
          )}
          
          <Box gridColumn="span 12">
            <TextField
              fullWidth
              size="small"
              multiline
              rows={2}
              label="Description"
              value={formData.description || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Additional notes or description"
            />
          </Box>
        </Box>

        {/* Lines Section - only for bills and invoices */}
        {showLines && (
          <>
            <Typography variant="h6" gutterBottom>Line Items</Typography>
            {formData.lines.map((line, index) => (
              <Box key={index} display="grid" gridTemplateColumns="2fr 1fr 1fr 1fr auto" gap={2} mb={2}>
                <TextField
                  size="small"
                  label="Description"
                  value={line.description || ''}
                  onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                  placeholder="Enter item description"
                />
                <TextField
                  size="small"
                  type="number"
                  label="Quantity"
                  value={line.quantity || 1}
                  onChange={(e) => handleLineChange(index, 'quantity', e.target.value)}
                  inputProps={{ min: 0, step: 'any' }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Unit Price"
                  value={line.unitPrice || 0}
                  onChange={(e) => handleLineChange(index, 'unitPrice', e.target.value)}
                  inputProps={{ min: 0, step: 'any' }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Tax %"
                  value={line.taxRate || 0}
                  onChange={(e) => handleLineChange(index, 'taxRate', e.target.value)}
                  inputProps={{ min: 0, max: 100, step: 'any' }}
                />
                <IconButton 
                  onClick={() => removeLine(index)} 
                  size="small" 
                  color="error"
                  disabled={formData.lines.length === 1}
                >
                  <Delete />
                </IconButton>
              </Box>
            ))}
            
            <Button onClick={addLine} startIcon={<Add />} sx={{ mb: 2 }}>
              Add Line Item
            </Button>

            {/* Totals Section */}
            <Box display="flex" justifyContent="flex-end">
              <Box minWidth={250} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Subtotal:</Typography>
                  <Typography fontWeight="bold">{fmtMoney(totals.sub)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Tax:</Typography>
                  <Typography fontWeight="bold">{fmtMoney(totals.tax)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" sx={{ borderTop: 1, borderColor: 'divider', pt: 1 }}>
                  <Typography variant="h6">Total:</Typography>
                  <Typography variant="h6" fontWeight="bold">{fmtMoney(totals.total)}</Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={formLoading}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={formLoading}
          startIcon={formLoading ? <CircularProgress size={16} /> : null}
        >
          {formLoading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Document Cards Component with API Integration
const DocumentCards = ({ data, onEdit, onDelete, onPost, loading = false }) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'draft': return 'default';
      case 'open': return 'warning';
      case 'posted': return 'success';
      case 'paid': return 'info';
      default: return 'default';
    }
  };

  const getDocumentNumber = (doc) => {
    return doc.billNumber || doc.invoiceNumber || doc.paymentNumber || doc.receiptNumber || doc.number || doc.creditNoteNumber || doc.debitNoteNumber || 'N/A';
  };

  const getDocumentDate = (doc) => {
    const date = doc.billDate || doc.invoiceDate || doc.paymentDate || doc.receiptDate || doc.date || doc.creditNoteDate || doc.debitNoteDate;
    return date ? new Date(date).toLocaleDateString() : '';
  };

  const getPartnerName = (doc) => {
    return doc.vendorName || doc.customerName || doc.vendor || doc.customer || doc.partner || 'N/A';
  };

  if (loading) {
    return <LoadingSpinner message="Loading documents..." />;
  }

  if (data.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="h6" color="text.secondary">
          No documents found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create your first document using the "New" button above
        </Typography>
      </Box>
    );
  }

  return (
    <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(350px, 1fr))" gap={2}>
      {data.map((doc, index) => {
        const totals = doc.totalAmount || doc.amount ? 
          { sub: doc.subtotalAmount || 0, tax: doc.taxAmount || 0, total: doc.totalAmount || doc.amount } :
          computeTotals(doc);
        
        return (
          <Box
            key={doc.id || index}
            sx={{
              p: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.paper',
              boxShadow: 1,
              '&:hover': { boxShadow: 3 },
              transition: 'box-shadow 0.2s'
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Box>
                <Typography variant="h6" component="span">
                  {getDocumentNumber(doc)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  {getDocumentDate(doc)}
                  {doc.dueDate && ` → ${new Date(doc.dueDate).toLocaleDateString()}`}
                </Typography>
              </Box>
              <Chip
                label={doc.status || 'Draft'}
                color={getStatusColor(doc.status)}
                size="small"
              />
            </Box>
            
            <Typography variant="body2" color="text.secondary" mb={1}>
              {getPartnerName(doc)}
            </Typography>

            {doc.reference && (
              <Typography variant="body2" color="text.secondary" mb={1}>
                Ref: {doc.reference}
              </Typography>
            )}

            {doc.description && (
              <Typography variant="body2" color="text.secondary" mb={1} 
                sx={{ 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  whiteSpace: 'nowrap' 
                }}>
                {doc.description}
              </Typography>
            )}
            
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Box>
                {totals.sub > 0 && (
                  <Typography variant="body2">
                    Subtotal: <strong>{fmtMoney(totals.sub)}</strong>
                  </Typography>
                )}
                {totals.tax > 0 && (
                  <Typography variant="body2">
                    Tax: <strong>{fmtMoney(totals.tax)}</strong>
                  </Typography>
                )}
              </Box>
              <Typography variant="h6">
                Total: <strong>{fmtMoney(totals.total)}</strong>
              </Typography>
            </Box>
            
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button size="small" variant="outlined" onClick={() => onEdit(doc)}>
                <Edit sx={{ fontSize: 16, mr: 0.5 }} /> Edit
              </Button>
              <Button 
                size="small" 
                variant="outlined" 
                color={doc.status === 'Draft' ? 'success' : 'warning'}
                onClick={() => onPost(doc)}
                disabled={loading}
              >
                {doc.status === 'Draft' ? 
                  <><PostAdd sx={{ fontSize: 16, mr: 0.5 }} /> Post</> : 
                  <><Undo sx={{ fontSize: 16, mr: 0.5 }} /> Revert</>
                }
              </Button>
              <Button 
                size="small" 
                variant="outlined" 
                color="error" 
                onClick={() => onDelete(doc)}
                disabled={loading}
              >
                <Delete sx={{ fontSize: 16 }} />
              </Button>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

// Main Document Page Component with API Integration
const ErpDocPage = ({ moduleKey }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [formLoading, setFormLoading] = useState(false); // ✅ ADDED: Missing state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const moduleConfig = Modules[moduleKey];
  const service = ServiceFactory.getService(moduleKey);

  // ✅ ADDED: Missing showSnackbar function
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // Load documents from API
  const loadDocuments = useCallback(async () => {
    if (!service) return;
    
    try {
      setLoading(true);
      setError('');
      
      const response = await service.getAll({ pageSize: 1000 }); // Get all documents
      
      if (response.success) {
        setDocs(response.data || []);
      } else {
        setError(response.message || 'Failed to load documents');
        setDocs([]);
      }
    } catch (err) {
      setError(err.message || 'Failed to load documents');
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [service]);

  // Initial load
  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // ✅ CORRECTED: Auto-code generation with proper state variable
  const handleAddNew = async () => {
    setFormLoading(true);
    
    try {
      // ✅ FIXED: Use moduleConfig.controllerRoute or moduleKey
      const controllerRoute = moduleConfig?.controllerRoute || moduleKey;
      const documentService = ServiceFactory.getService(controllerRoute);
      
      // ✅ Generate next document number
      const response = await documentService.getNextNumber(1); // companyId = 1
      
      console.log('Next Number Response:', response);
      
      if (response.success && response.data) {
        const newCode = response.data;
        const today = new Date().toISOString().split('T')[0];
        
        // Create initial document with auto-generated code
        const initialDocument = {
          // Set the document number field (varies by module)
          ...(moduleKey === 'vendor_bills' && { billNumber: newCode, billDate: today }),
          ...(moduleKey === 'customer_invoices' && { invoiceNumber: newCode, invoiceDate: today }),
          ...(moduleKey === 'vendor_payments' && { paymentNumber: newCode, paymentDate: today }),
          ...(moduleKey === 'customer_receipts' && { receiptNumber: newCode, receiptDate: today }),
          ...(moduleKey === 'credit_notes' && { creditNoteNumber: newCode, creditNoteDate: today }),
          ...(moduleKey === 'debit_notes' && { debitNoteNumber: newCode, debitNoteDate: today }),
          ...(moduleKey === 'journal_entries' && { entryNumber: newCode, entryDate: today }),
          
          // Initialize lines array (skip for payments/receipts)
          lines: moduleKey.includes('payment') || moduleKey.includes('receipt') ? [] : [{
            lineNumber: 1,
            description: '',
            quantity: 1,
            unitPrice: 0,
            taxRate: 0,
            amount: 0
          }],
          
          // Set default status
          status: 'Draft',
          
          // Company ID
          companyId: 1,
          
          // Initialize other fields
          reference: '',
          referenceDocument: '',
          description: '',
          dueDate: '',
          vendorId: null,
          customerId: null,
          paymentMethod: '',
          amount: 0
        };
        
        setEditingData(initialDocument);
        setFormOpen(true);
      } else {
        showSnackbar(response.message || 'Could not generate document number', 'error');
      }
    } catch (error) {
      console.error('Error generating document number:', error);
      showSnackbar('Error generating document number', 'error');
    }
    
    setFormLoading(false);
  };

  const handleEdit = (doc) => {
    console.log("Edit", doc);
    setEditingData(doc);
    setFormOpen(true);
  };

  const handleDelete = async (doc) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      setLoading(true);
      const response = await service.delete(doc.id);
      
      if (response.success) {
        showSnackbar('Document deleted successfully', 'success');
        await loadDocuments(); // Reload documents
      } else {
        showSnackbar(response.message || 'Failed to delete document', 'error');
      }
    } catch (err) {
      showSnackbar(err.message || 'Failed to delete document', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePost = async (doc) => {
    try {
      setLoading(true);
      const updatedDoc = {
        ...doc,
        status: doc.status === 'Draft' ? 'Posted' : 'Draft'
      };
      
      const response = await service.update(doc.id, updatedDoc);
      
      if (response.success) {
        showSnackbar(
          `Document ${doc.status === 'Draft' ? 'posted' : 'reverted'} successfully`,
          'success'
        );
        await loadDocuments(); // Reload documents
      } else {
        showSnackbar(response.message || 'Failed to update document', 'error');
      }
    } catch (err) {
      showSnackbar(err.message || 'Failed to update document', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData) => {
    try {
      let response;
      
      if (editingData && editingData.id) {
        // Update existing document
        response = await service.update(editingData.id, formData);
      } else {
        // Create new document
        console.log("Creating document with data:", JSON.stringify(formData));
        response = await service.create(formData);
      }
      
      if (response.success) {
        showSnackbar(
          editingData && editingData.id ? 'Document updated successfully' : 'Document created successfully',
          'success'
        );
        
        setFormOpen(false);
        setEditingData(null);
        await loadDocuments(); // Reload documents
      } else {
        throw new Error(response.message || 'Failed to save document');
      }
    } catch (error) {
      showSnackbar(error.message || 'Failed to save document', 'error');
      throw error; // Re-throw so form can handle it
    }
  };

  const handleRefresh = () => {
    loadDocuments();
  };

  const handleExport = () => {
    const csvData = docs.map(doc => {
      const totals = doc.totalAmount ? 
        { sub: doc.subtotalAmount || 0, tax: doc.taxAmount || 0, total: doc.totalAmount } :
        computeTotals(doc);
        
      return {
        Number: doc.billNumber || doc.invoiceNumber || doc.paymentNumber || doc.receiptNumber || doc.creditNoteNumber || doc.debitNoteNumber,
        Date: doc.billDate || doc.invoiceDate || doc.paymentDate || doc.receiptDate || doc.creditNoteDate || doc.debitNoteDate,
        DueDate: doc.dueDate,
        Partner: doc.vendorName || doc.customerName,
        Status: doc.status,
        Subtotal: totals.sub,
        Tax: totals.tax,
        Total: totals.total,
        Reference: doc.reference || doc.referenceDocument,
        Description: doc.description
      };
    });

    const headers = Object.keys(csvData[0] || {});
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${moduleConfig?.title?.replace(/\s+/g, '_').toLowerCase() || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Calculate summary statistics
  const openDocs = docs.filter(doc => doc.status !== 'Paid').length;
  const totalAmount = docs.reduce((sum, doc) => {
    const totals = doc.totalAmount ? 
      { total: doc.totalAmount } :
      computeTotals(doc);
    return sum + totals.total;
  }, 0);

  if (!moduleConfig) {
    return (
      <Box p={3}>
        <Typography color="error" variant="h6">
          Error: Module "{moduleKey}" not found in configuration.
        </Typography>
      </Box>
    );
  }

  return (
    <div className="content">
      <div className="page-header d-flex mb-3">
        <h2 className="page-heading fs-7">{moduleConfig?.title}</h2>
      </div>
      
      {/* Quick Stats */}
      <Box display="flex" gap={2} mb={3}>
        <Box 
          sx={{ 
            p: 2, 
            bgcolor: 'primary.light', 
            borderRadius: 2, 
            minWidth: 150,
            textAlign: 'center'
          }}
        >
          <Typography variant="h4" color="primary.contrastText">{docs.length}</Typography>
          <Typography variant="body2" color="primary.contrastText">Total Documents</Typography>
        </Box>
        <Box 
          sx={{ 
            p: 2, 
            bgcolor: 'warning.light', 
            borderRadius: 2, 
            minWidth: 150,
            textAlign: 'center'
          }}
        >
          <Typography variant="h4" color="warning.contrastText">{openDocs}</Typography>
          <Typography variant="body2" color="warning.contrastText">Open Documents</Typography>
        </Box>
        <Box 
          sx={{ 
            p: 2, 
            bgcolor: 'success.light', 
            borderRadius: 2, 
            minWidth: 200,
            textAlign: 'center'
          }}
        >
          <Typography variant="h5" color="success.contrastText">{fmtMoney(totalAmount)}</Typography>
          <Typography variant="body2" color="success.contrastText">Total Amount</Typography>
        </Box>
      </Box>

      <div className="card-soft p-3 p-md-4">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h5">{moduleConfig?.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {moduleConfig?.subtitle}
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Button 
              onClick={handleRefresh} 
              startIcon={<Refresh />}
              variant="outlined"
              size="small"
              disabled={loading}
            >
              Refresh
            </Button>
            <Button 
              onClick={handleExport} 
              startIcon={<GetApp />}
              variant="outlined"
              size="small"
              disabled={loading || docs.length === 0}
            >
              Export CSV
            </Button>
            <Button 
              onClick={() => window.print()} 
              startIcon={<Print />}
              variant="outlined"
              size="small"
            >
              Print
            </Button>
            <Button 
              onClick={handleAddNew} 
              variant="contained" 
              startIcon={<Add />}
              size="small"
              disabled={loading || formLoading}
            >
              New {moduleConfig?.title?.slice(0, -1)}
            </Button>
          </Box>
        </Box>

        {/* Error Display */}
        {error && (
          <ErrorAlert error={error} onRetry={handleRefresh} />
        )}

        {/* Document Cards */}
        <DocumentCards
          data={docs}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onPost={handlePost}
          loading={loading}
        />
      </div>

      {/* Document Form Modal */}
      <DocumentForm
        moduleKey={moduleKey}
        initialData={editingData}
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingData(null);
        }}
        onSave={handleSave}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ErpDocPage;